import { Injectable } from '@angular/core';
import { getDocs, query, where } from 'firebase/firestore';
import moment from 'moment';

import { ownedCollection } from '../../../utils/firestore-ownership';
import { CompaniesService } from '../companies/companies.service';
import { SharesService } from '../shares/shares.service';
import { StocksService } from '../stocks/stocks.service';
import { ReitsService } from '../reits/reits.service';
import {
  ImportAsset, ImportDraft, ImportResult, ImportShareType, ImportTrade, NEW_RECORD, StatementPreview
} from './statement-reader.model';

// Share class and listing segment suffixes of B3 titles, e.g. "EZTEC ON NM", "ISA ENERGIA PN N1"
const TITLE_SUFFIXES = new Set([
  'ON', 'PN', 'PNA', 'PNB', 'PNC', 'PND', 'UNT', 'UNIT', 'CI', 'DR1', 'DR2', 'DR3',
  'NM', 'N1', 'N2', 'MA', 'MB', 'ER', 'EJ', 'ED', 'EDJ', 'EB', 'ES', 'EDR', 'ATZ', 'REC'
]);
const SEGMENTS = new Set(['NM', 'N1', 'N2', 'MA', 'MB']);

export interface ImportContext {
  companies: { code: string, name: string }[],
  shares: { code: string, alias: string, company: string, type: string }[]
}

@Injectable({
  providedIn: 'root'
})
export class StatementImportService {

  constructor(
    private companiesService: CompaniesService,
    private sharesService: SharesService,
    private stocksService: StocksService,
    private reitsService: ReitsService
  ) { }

  async loadContext(): Promise<ImportContext> {
    const [companies, shares] = await Promise.all([
      getDocs(ownedCollection('Companies')),
      getDocs(ownedCollection('Shares'))
    ]);

    return {
      companies: companies.docs
        .map((d: any) => ({ code: d.data().code, name: d.data().name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      shares: shares.docs
        .map((d: any) => ({ code: d.data().code, alias: d.data().alias ?? '', company: d.data().company, type: d.data().type }))
        .filter(s => s.type == 'STOCK' || s.type == 'REIT')
        .sort((a, b) => a.code.localeCompare(b.code))
    };
  }

  // Operations already created from this note number
  async countImported(noteNumber: string): Promise<number> {
    if (!noteNumber) return 0;

    const [stocks, reits] = await Promise.all([
      getDocs(query(ownedCollection('Stocks'), where('note', '==', noteNumber))),
      getDocs(query(ownedCollection('Reits'), where('note', '==', noteNumber)))
    ]);
    return stocks.size + reits.size;
  }

  buildDraft(preview: StatementPreview, context: ImportContext, alreadyImported: number): ImportDraft {
    const assets = new Map<string, ImportAsset>();
    for (const trade of preview.trades) {
      if (!assets.has(trade.title))
        assets.set(trade.title, this.buildAsset(trade.title, context));
    }

    const taxes = allocateFees(preview);
    const trades: ImportTrade[] = preview.trades.map((trade, i) => {
      const asset = assets.get(trade.title)!;
      return { trade, asset, allocatedTax: taxes[i], tax: taxOf(asset, taxes[i]) };
    });

    return {
      noteNumber: preview.noteNumber,
      date: preview.date ? moment(preview.date, 'DD/MM/YYYY').toDate() : null,
      assets: [...assets.values()],
      trades,
      alreadyImported
    };
  }

  // Called when an asset changes between STOCK and REIT
  applyTaxes(draft: ImportDraft): void {
    for (const item of draft.trades)
      item.tax = taxOf(item.asset, item.allocatedTax);
  }

  private buildAsset(title: string, context: ImportContext): ImportAsset {
    const share = findShare(title, context.shares);
    const companyName = guessCompanyName(title);
    const company = context.companies.find(c => normalize(c.name) == normalize(companyName));

    return {
      title,
      type: (share?.type as ImportShareType) ?? guessType(title),
      shareCode: share?.code ?? NEW_RECORD,
      matchedShare: share != undefined,
      newShare: { code: '', alias: title, dividends: true },
      companyCode: company?.code ?? NEW_RECORD,
      matchedCompany: company != undefined,
      newCompany: { name: companyName, code: '' }
    };
  }

  // Messages describing what still needs to be filled before generating
  validate(draft: ImportDraft, context: ImportContext): string[] {
    const errors: string[] = [];
    const newShareCodes = new Set<string>();
    const newCompanies = new Map<string, string>();

    if (!draft.date) errors.push('Data do pregão não encontrada na nota.');

    for (const asset of draft.assets) {
      if (!asset.shareCode) {
        errors.push(`${asset.title}: escolha um papel.`);
        continue;
      }
      if (asset.shareCode != NEW_RECORD) continue;

      const code = normalize(asset.newShare.code);
      if (!code) errors.push(`${asset.title}: informe o código do papel novo.`);
      else if (context.shares.some(s => normalize(s.code) == code)) errors.push(`${asset.title}: o papel ${code} já existe, selecione-o na lista.`);
      else if (newShareCodes.has(code)) errors.push(`${asset.title}: o código ${code} foi usado em outro papel novo.`);
      newShareCodes.add(code);

      if (!asset.companyCode) errors.push(`${asset.title}: escolha uma empresa.`);
      if (asset.companyCode != NEW_RECORD) continue;

      const name = normalize(asset.newCompany.name);
      const companyCode = normalize(asset.newCompany.code);
      if (!name || !companyCode) errors.push(`${asset.title}: informe nome e código da empresa nova.`);
      else if (context.companies.some(c => normalize(c.code) == companyCode)) errors.push(`${asset.title}: a empresa ${companyCode} já existe, selecione-a na lista.`);
      // The same new company can back more than one share (e.g. ON and PN)
      else if (newCompanies.has(companyCode) && newCompanies.get(companyCode) != name) errors.push(`${asset.title}: o código ${companyCode} foi usado para outra empresa nova.`);
      newCompanies.set(companyCode, name);
    }

    return errors;
  }

  async commit(draft: ImportDraft, context: ImportContext): Promise<ImportResult> {
    const result: ImportResult = { companies: 0, shares: 0, stocks: 0, reits: 0 };
    const shareTypes = new Map(context.shares.map(s => [s.code, s.type as ImportShareType]));
    const createdCompanies = new Set<string>();

    for (const asset of draft.assets) {
      if (asset.shareCode != NEW_RECORD) continue;

      let companyCode = asset.companyCode;
      if (companyCode == NEW_RECORD) {
        companyCode = normalize(asset.newCompany.code);
        if (!createdCompanies.has(companyCode)) {
          await this.companiesService.addItem({ name: asset.newCompany.name.trim(), code: companyCode, active: true, img: '' });
          createdCompanies.add(companyCode);
          result.companies++;
        }
      }

      const shareCode = normalize(asset.newShare.code);
      await this.sharesService.addItem({
        company: companyCode,
        code: shareCode,
        alias: asset.newShare.alias.trim(),
        type: asset.type,
        site: '',
        dividends: asset.newShare.dividends
      });
      shareTypes.set(shareCode, asset.type);
      result.shares++;
    }

    for (const item of draft.trades) {
      const shareCode = item.asset.shareCode == NEW_RECORD ? normalize(item.asset.newShare.code) : item.asset.shareCode;
      const operation = {
        share: shareCode,
        date: draft.date,
        side: item.trade.side == 'C' ? 'BUY' : 'SELL',
        amount: item.trade.quantity.toString(),
        price: item.trade.price,
        note: draft.noteNumber
      };

      // Same shape as the manual forms: REITs have no tax
      if (shareTypes.get(shareCode) == 'REIT') {
        await this.reitsService.addItem({ ...operation, total: item.trade.total });
        result.reits++;
      } else {
        await this.stocksService.addItem({ ...operation, tax: item.tax, total: round2(item.trade.total + item.tax) });
        result.stocks++;
      }
    }

    return result;
  }
}

export function normalize(value: string | undefined): string {
  return (value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim().toUpperCase();
}

// Share alias equal to the note title, with or without the listing segment
export function findShare<T extends { code: string, alias: string }>(title: string, shares: T[]): T | undefined {
  const tokens = normalize(title).split(' ');
  const withoutSegment = SEGMENTS.has(tokens[tokens.length - 1]) ? tokens.slice(0, -1).join(' ') : tokens.join(' ');
  const candidates = new Set([tokens.join(' '), withoutSegment]);

  return shares.find(s => candidates.has(normalize(s.alias)) || candidates.has(normalize(s.code)));
}

// "ISA ENERGIA PN N1" -> "ISA ENERGIA", "FII XP LOG CI" -> "XP LOG"
export function guessCompanyName(title: string): string {
  const tokens = normalize(title).split(' ');
  if (tokens[0] == 'FII' && tokens.length > 1) tokens.shift();
  while (tokens.length > 1 && TITLE_SUFFIXES.has(tokens[tokens.length - 1])) tokens.pop();
  return tokens.join(' ');
}

export function guessType(title: string): ImportShareType {
  const tokens = normalize(title).split(' ');
  return tokens[0] == 'FII' || tokens.includes('CI') ? 'REIT' : 'STOCK';
}

// Splits every note cost (settlement, fees, brokerage, taxes) across trades by traded value.
// Costs are the difference between the net amount and the value of the operations.
export function allocateFees(preview: StatementPreview): number[] {
  const trades = preview.trades;
  const tradedValue = trades.reduce((sum, t) => sum + t.total, 0);
  if (!trades.length || !preview.netValue || !tradedValue) return trades.map(() => 0);

  const operations = trades.reduce((sum, t) => sum + (t.side == 'V' ? t.total : -t.total), 0);
  const net = preview.netDebitCredit == 'C' ? preview.netValue : -preview.netValue;
  const fees = round2(operations - net);
  if (fees <= 0) return trades.map(() => 0);

  const taxes = trades.map(t => round2(fees * t.total / tradedValue));
  // Rounding remainder goes to the last trade so the sum matches the note
  taxes[taxes.length - 1] = round2(fees - taxes.slice(0, -1).reduce((sum, t) => sum + t, 0));
  return taxes;
}

function taxOf(asset: ImportAsset, allocatedTax: number): number {
  return asset.type == 'REIT' ? 0 : allocatedTax;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
